/**
 * cotejarFirmantes.js — comparar quién firma con quién asistió.
 *
 * ── Por qué avisa y no impide ────────────────────────────────────────────
 *
 * Porque las dos listas son legítimamente distintas a veces. Firma el director
 * que no fue a la reunión; asiste alguien que estaba de oyente y no tiene que
 * firmar nada. Bloquear el envío por eso pararía actas correctas, y quien tiene
 * prisa acabaría metiendo nombres de relleno en la tabla para poder mandarla,
 * que es peor que el problema.
 *
 * Pero callarlo tampoco vale. La firma dibujada se estampa en la fila de esa
 * persona dentro de la tabla de asistentes: si no tiene fila, su rúbrica no
 * aparece en el acta. Firma, queda registrado que firmó, y el documento sale sin
 * su firma. Enterarse de eso al final —cuando los correos ya salieron— es
 * enterarse tarde.
 *
 * Así que se dice antes de mandar, y se deja mandar.
 *
 * ── Esta regla tiene un gemelo en el servidor ────────────────────────────
 *
 * `azure-functions/v2/fill/FirmaEnDocx.js` usa la misma para decidir en qué
 * fila va cada rúbrica. Tienen que coincidir: si aquí se dice que Fulano encaja
 * y allí no, el aviso diría que todo está bien y luego la firma no aparecería.
 *
 * No se comparte el código porque uno es ESM del navegador y el otro CommonJS
 * de las funciones. Lo que las mantiene en paso es `probar-firma-en-acta.js`,
 * que carga las dos y comprueba que contestan igual sobre los mismos casos.
 */

/** Sin acentos, sin mayúsculas y sin dobles espacios. */
export function llano(texto) {
  return String(texto || '')
    .normalize('NFD').replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    // Sin puntuacion. El directorio devuelve «Espadas Espinosa, Jorge Ivan» y el
    // acta «Jorge Ivan Espadas Espinosa»: sin quitar la coma, «espinosa,» no
    // encaja con «espinosa» y la firma de esa persona se queda fuera del acta.
    .replace(/[.,;:]/g, ' ')
    .replace(/\s+/g, ' ').trim();
}

/**
 * ¿Son la misma persona estos dos nombres?
 *
 * No se exige coincidencia letra a letra. En el acta pone «Jorge Iván Espadas
 * Espinosa» y el directorio devuelve «Espadas Espinosa, Jorge Iván»: la misma
 * persona, y pedir igualdad exacta dejaría su firma fuera.
 *
 * Se comparan los trozos: si todos los de uno están en el otro, es él. Sirve
 * para los dos órdenes y aguanta un apellido de más.
 *
 * Lo que NO se acepta es un solo trozo coincidente. «García» encaja con media
 * empresa, y poner la firma de alguien en la fila de otro es peor que no
 * ponerla: el acta diría que firmó quien no firmó.
 */
export function mismoNombre(a, b) {
  const unoA = llano(a).split(' ').filter(p => p.length > 1);
  const unoB = llano(b).split(' ').filter(p => p.length > 1);
  if (unoA.length === 0 || unoB.length === 0) return false;

  const corto = unoA.length <= unoB.length ? unoA : unoB;
  const largo = corto === unoA ? unoB : unoA;

  if (corto.length < 2) return false;
  return corto.every(p => largo.includes(p));
}

/**
 * Qué no cuadra entre firmantes y asistentes.
 *
 * @param {Array} firmantes - [{ nombre, correo }]
 * @param {Array} asistentes - los nombres de la tabla del acta
 * @returns {{sinFila: string[], sinFirmar: string[], cuadran: boolean}}
 *
 * `sinFila` son los que firman y no están en la tabla: su rúbrica no va a tener
 * dónde estamparse. Es el aviso que importa.
 *
 * `sinFirmar` son los que asistieron y no se les pide firma. Es información, no
 * un problema: puede ser justo lo que se quería.
 */
export function cotejar(firmantes = [], asistentes = []) {
  const nombres = (asistentes || []).map(a => String(a || '').trim()).filter(Boolean);
  const gente = (firmantes || []).filter(f => f && f.nombre);

  const sinFila = gente
    .filter(f => !nombres.some(n => mismoNombre(n, f.nombre)))
    .map(f => f.nombre);

  const sinFirmar = nombres
    .filter(n => !gente.some(f => mismoNombre(n, f.nombre)));

  return { sinFila, sinFirmar, cuadran: sinFila.length === 0 && sinFirmar.length === 0 };
}

const cotejarFirmantes = { llano, mismoNombre, cotejar };
export default cotejarFirmantes;
